import { useMonthlyPayment } from "app/hooks/checkout/useMonthlyPayment";
import { useCartContext } from "app/providers/CartProvider";
import { useConfigurationOptionsContext } from "app/providers/DoorOptionsProvider";
import ProductModal, { ProductModalType } from "entities/shared/product-modal";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CreateCartPayload, Product } from "shared/api/middleware-api-client";
import { selfAssemblyInputState } from "shared/store/selfAssembly/selfAssemblyInputs";
import { getFormattedPrice } from "shared/utils/getFormattedPrice";
import { useSnapshot } from "valtio";
import { DoorOptionsFooterMobile } from "./components/DoorOptionsFooterMobile";
import { DoorOptionsFooterNormal } from "./components/DoorOptionsFooterNormal";
import useUpdateZakelijkPayload from "app/hooks/checkout/useUpdateZakelijkPayload";
import { STORAGE_PERSONAL_INFO_TYPE } from "shared/config";
import { zakelijkProfielCodes } from "features/option-controls/door-width-controls/ZakelijkHelper";
import BusinessProductModal from "./components/BusinessProductModal";

const MIN_MONTHLY_PRICE = 250;
const MAX_MONTHLY_PRICE = 10000;
const DoorOptionsFooter = () => {
  const {
    selectedDoor,
    layout,
    color,
    glass,
    handle,
    getConfigurationPrice,
    getConfigurationProduct,
    showBeamHeightValidationError,
  } = useConfigurationOptionsContext();
  const { addCartProduct, getCart, isSampleSale } = useCartContext();

  const { updateZakelijkPayload } = useUpdateZakelijkPayload();
  const [searchParams, setSearchParams] = useSearchParams();

  //Configuration ID is used to swap a product in the cart
  const configurationId: string | null = searchParams.get("configuration");
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

  // Get the monthly payment amount
  const { data } = useMonthlyPayment(getConfigurationPrice());
  const monthlyPayment = data?.monthlyPayment;

  const selfAssemblyInputsSnapshot = useSnapshot(selfAssemblyInputState);

  // Get the configured door product
  const product = getConfigurationProduct();

  // Check if monthly payment fits the range criteria
  const isMonthlyPaymentAvailable = useMemo(() => {
    const monthlyPrice = getConfigurationPrice();
    return (
      monthlyPrice >= MIN_MONTHLY_PRICE && monthlyPrice <= MAX_MONTHLY_PRICE
    );
  }, [getConfigurationPrice]);

  const validateOptions = (): boolean => {
    let errors: string[] = [];
    // TODO: Add correct error strings
    if (!selectedDoor) {
      errors.push("deur");
    }

    if (!layout) {
      errors.push("ontwerp");
    }

    if (!color) {
      errors.push("kleur");
    }

    if (!glass) {
      errors.push("glas");
    }

    if (!handle) {
      errors.push("deurbeslag");
    }

    if (errors.length === 0) {
      return true;
    }

    const error = `Je hebt nog geen ${errors.reduce(
      (text, value, i, array) =>
        text + (i < array.length - 1 ? ", " : " en ") + value,
    )}
      geselecteerd. Maak een keuze in het menu voordat je verder gaat.`;
    toast.error(error);

    return false;
  };

  const errorAssemblyAddition = (
    cart: CreateCartPayload,
    productToAdd: Product,
  ) => {
    if (cart.products.length > 0) {
      if (
        cart.isSelfAssembly === false &&
        productToAdd.isSelfAssembly === undefined
      ) {
        return false;
      }
      return cart.isSelfAssembly !== productToAdd.isSelfAssembly;
    }
  };

  const canProceed = (productToAdd: Product) => {
    const cart = getCart();
    if (cart.standardProducts.length > 0) {
      toast.info(
        "Je kan geen deur toevoegen aan de winkelwagen, omdat je andere producttypes in de winkelwagen hebt zitten.",
      );
      return false;
    }

    if (errorAssemblyAddition(cart, productToAdd)) {
      toast.info(
        "Je kunt geen deur met montageservice aan je winkelwagen toevoegen omdat je bij een ander product al hebt gekozen voor `zelf monteren`.",
      );
      return false;
    }
    if (isSampleSale) {
      toast.info("Cannot add this door when sample sale is on");
      return false;
    }
    if (showBeamHeightValidationError) {
      toast.error(
        "Let op: maximale lengte greep is 2100mm. Pas het ontwerp eerst aan.",
      );
      return false;
    }
    return true;
  };

  const selfAssemblyWidthHeightValidation = () => {
    if (!selfAssemblyInputsSnapshot.widthInput) {
      toast.info("Let op: de breedte van de deur is niet geschikt.");
      return false;
    }

    if (!selfAssemblyInputsSnapshot.heightInput) {
      toast.info("Let op: de hoogte van de deur is niet geschikt.");
      return false;
    }
    return true;
  };

  const validateDoorOptions = (product: Product | null) => {
    if (!validateOptions()) return;
    if (!product) return;
    if (!canProceed(product)) return;

    if (product.isSelfAssembly) {
      if (!selfAssemblyWidthHeightValidation()) return;
    }
  };

  const addToCart = () => {
    if (!selectedDoor) return;

    // ID - when a product from the cart is swapped
    let id: number | undefined = Number(configurationId);
    if (!configurationId || isNaN(id)) id = undefined;

    // Validate door options for any business door besides the profiles ( profiel )
    if (!zakelijkProfielCodes.includes(selectedDoor.code)) {
      validateDoorOptions(product);
    }

    // Add to cart, set to business
    if (!product) return;
    const businessProduct = { ...product };

    updateZakelijkPayload(product.doorType.code, businessProduct);

    if (!addCartProduct(businessProduct, id)) return;
    localStorage.setItem(STORAGE_PERSONAL_INFO_TYPE, "business");
    setConfirmationModalOpen(true);
  };
  const footersGridElement = document.getElementById("footersGridElements");

  if (!selectedDoor) return null;

  return (
    <>
      {product && (
        <BusinessProductModal
          isOpen={isConfirmationModalOpen}
          setIsOpen={setConfirmationModalOpen}
          monthlyPayment={monthlyPayment!}
          totalPrice={getFormattedPrice(getConfigurationPrice())}
          product={product}
        />
      )}

      {footersGridElement &&
        createPortal(
          <div className="gg-fixed gg-bottom-0 gg-left-0 gg-z-10 gg-mx-auto gg-hidden gg-w-full lg:gg-block">
            <DoorOptionsFooterNormal
              selectedDoor={selectedDoor}
              isMonthlyPaymentAvailable={isMonthlyPaymentAvailable}
              monthlyPayment={monthlyPayment}
              addToCart={addToCart}
            />
          </div>,
          footersGridElement,
        )}

      {footersGridElement &&
        createPortal(
          <div className="gg-fixed gg-bottom-0 gg-left-0 gg-z-10 gg-order-last gg-col-span-12 gg-mx-auto gg-w-full gg-flex-col gg-bg-dark-blue lg:gg-hidden">
            <DoorOptionsFooterMobile
              selectedDoor={selectedDoor}
              isMonthlyPaymentAvailable={isMonthlyPaymentAvailable}
              monthlyPayment={monthlyPayment}
              addToCart={addToCart}
            />
          </div>,
          footersGridElement,
        )}
    </>
  );
};
export default DoorOptionsFooter;
